/**
 * HomePage — 포트폴리오 컨셉 쇼케이스
 * 에디토리얼 타이포그래피 + 넘버링된 Selected Work + 전체 프로젝트 아카이브 색인
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import { useAuth } from '../hooks/useAuth';

/* ── 스크롤 리빌 ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, vis] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ── Selected Work — 대표 프로젝트 6 ─── */
const FEATURED = [
  {
    no: '01',
    category: 'REALTIME · LLM',
    title: 'AI 토론 배틀',
    desc: '서로 다른 페르소나의 AI 파이터들이 라운드제로 맞붙는 실시간 토론 아레나. SSE 스트리밍으로 문장이 생성되는 순간을 그대로 중계하고, 종료 후 상위 모델이 판정합니다.',
    stack: ['Gemini 2.5', 'SSE Streaming', 'Flask', 'MySQL'],
    to: '/battles',
    accent: '#a78bfa',
  },
  {
    no: '02',
    category: 'COMPUTER VISION',
    title: 'AI 비전 랩',
    desc: '반려동물 사진 정밀 분석, 실종·발견 매칭, 얼굴 유사도 검색. 멀티모달 모델과 32차원 특징 벡터로 유기동물 구조를 돕는 비전 파이프라인.',
    stack: ['Multimodal', 'Feature Vector', 'Vision API'],
    to: '/vision-lab',
    accent: '#22d3ee',
  },
  {
    no: '03',
    category: 'PUBLIC DATA × AI',
    title: '농사직설 AI Pro',
    desc: '농촌진흥청 120+ 공공 API와 기상·시세 데이터를 결합한 영농 인텔리전스. AI 작물 진단, 재배 가이드, 24절기 스마트 캘린더까지 하나의 대시보드로.',
    stack: ['공공데이터 120+', 'KAMIS', 'Gemini 2.5', 'Recharts'],
    to: '/farming',
    accent: '#34d399',
  },
  {
    no: '04',
    category: 'GIS · LIVE OPS',
    title: '실시간 교통 관제',
    desc: 'ITS 국가교통정보센터 CCTV를 지도 위에 실시간 스트리밍하고, 기상·교통 데이터를 AI가 분석해 배송 경로를 최적화하는 관제 시스템.',
    stack: ['ITS CCTV', 'Leaflet', 'HLS.js', 'AI 경로분석'],
    to: '/traffic-monitor',
    accent: '#fbbf24',
  },
  {
    no: '05',
    category: 'COMMERCE · DATA',
    title: '모비딕 프루트 마켓',
    desc: '농림위성·산지 데이터 기반 과일 큐레이션 커머스. AI 신선도 스캐너와 산지 위성 분석으로 데이터가 검증한 과일만 골라냅니다.',
    stack: ['위성 데이터', 'AI 스캐너', 'SES 알림'],
    to: '/fruit-market',
    accent: '#fb7185',
  },
  {
    no: '06',
    category: 'ARCHITECTURE',
    title: '자율 에이전트 코어 — 기술 백서',
    desc: 'LLM 멀티턴 자율 실행 루프, 실시간 텔레메트리, 동적 도구 레지스트리. 이 플랫폼 전체를 움직이는 에이전트 아키텍처의 설계 문서.',
    stack: ['Agent Loop', 'Tool Registry', 'Telemetry'],
    to: '/tech',
    accent: '#818cf8',
  },
];

/* ── 아카이브 색인 — 전체 프로젝트 ─── */
const ARCHIVE = [
  { title: '논문탐색 arXiv 대시보드', cat: 'Research', to: '/arxiv' },
  { title: 'AEGIS 산업안전 시뮬레이터', cat: 'Simulation', to: '/aegis' },
  { title: '농산물 시세 · 유가 대시보드', cat: 'Data Viz', to: '/market' },
  { title: '농업 인텔리전스 센터', cat: 'Data Viz', to: '/agri-intelligence' },
  { title: '24절기 영농 캘린더', cat: 'Public Data', to: '/agri-calendar' },
  { title: '과일 배송 물류 관제', cat: 'Live Ops', to: '/dispatch-logistics' },
  { title: 'Discord GameHub 봇', cat: 'Discord Bot', to: '/discord-gamebot' },
  { title: '냠냠봇 관리자 위키', cat: 'Docs', to: '/discord-gamebot/admin-guide' },
  { title: 'Aifighter CLI', cat: 'Terminal UI', to: '/cli' },
  { title: '묘냥의 숲', cat: 'Game', to: '/game/forest' },
  { title: 'AI 도구 모음', cat: 'Utility', to: '/ai-tools' },
  { title: '유기동물 구조센터', cat: 'Vision · Community', to: '/rescue-lab' },
  { title: '커뮤니티', cat: 'Community', to: '/community' },
  { title: 'AI 에이전트 챗', cat: 'Agent', to: '/agent' },
  { title: '상품 쇼케이스', cat: 'Commerce', to: '/products' },
  { title: '고양이 언어모델 편집기', cat: 'AI Editor', to: '/page-editor' },
  { title: '면접냥이', cat: 'AI Coach', to: '/interview' },
  { title: '음연 — 뮤지션 매칭', cat: 'Platform', to: '/musician' },
];

/* ── 기술 스택 ─── */
const STACK = [
  'React 18', 'Flask', 'Gemini 2.5 Flash/Pro', 'SSE', 'Socket.IO',
  'MySQL 8 (RDS)', 'AWS EC2', 'AWS SES', 'Leaflet', 'HLS.js',
  'Recharts', 'Fabric.js', 'Tailwind CSS', 'JWT + OAuth 2.0', 'Nginx · Gunicorn',
];

const STATS = [
  { value: '22+', label: '라이브 서비스' },
  { value: '120+', label: '연동 공공 API' },
  { value: '80+', label: 'CLI 명령어' },
  { value: '1', label: '에이전트 코어' },
];

export default function HomePage() {
  const { isAuthenticated } = useAuth();

  useMetaTags({
    title: 'AI Fighter — AI 프로덕트 포트폴리오',
    description: 'AI 토론 배틀부터 비전 랩, 공공데이터 영농 인텔리전스, 실시간 교통 관제까지 — 하나의 에이전트 코어로 움직이는 AI 프로덕트 쇼케이스.',
    image: '/cover.png',
    url: 'https://aifighter.io',
  });

  return (
    <div className="min-h-screen bg-[#050507] text-white selection:bg-cyan-500/30">
      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden">
        {/* 배경: 미세 그리드 + 코너 글로우 */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '72px 72px' }} />
        <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full blur-[160px] opacity-[0.13]" style={{ background: 'radial-gradient(circle,#7c3aed,transparent 70%)' }} />
        <div className="absolute -bottom-52 -right-32 w-[520px] h-[520px] rounded-full blur-[150px] opacity-[0.10]" style={{ background: 'radial-gradient(circle,#0891b2,transparent 70%)' }} />

        <div className="relative z-10 max-w-apple-wide mx-auto w-full px-6 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50">
              AI Product Engineering Portfolio
            </span>
          </div>

          <h1 className="font-bold tracking-[-0.04em] leading-[1.02] mb-8" style={{ fontSize: 'clamp(2.4rem,8vw,7rem)', wordBreak: 'keep-all' }}>
            하나의 에이전트 코어,
            <br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(100deg,#a855f7 0%,#6366f1 40%,#06b6d4 75%,#34d399 100%)' }}>
              22개의 AI 프로덕트.
            </span>
          </h1>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
            <p className="text-white/55 max-w-xl leading-relaxed" style={{ fontSize: 'clamp(1rem,1.6vw,1.15rem)' }}>
              실시간 AI 토론 아레나부터 컴퓨터 비전, 공공데이터 인텔리전스,
              라이브 관제 시스템까지 — 도메인이 바뀌어도 같은 자율 실행 루프가
              움직이는 풀스택 AI 쇼케이스입니다.
            </p>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <a href="#work"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black text-[14px] font-semibold rounded-full hover:bg-white/85 transition-colors">
                작업 보기
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </a>
              <Link to="/battles"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 hover:border-white/50 text-white text-[14px] font-medium rounded-full transition-colors">
                라이브 데모 — AI 토론
              </Link>
            </div>
          </div>

          {/* 스탯 스트립 */}
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10 mt-16">
            {STATS.map((s) => (
              <div key={s.label} className="py-6 pr-6 border-b md:border-b-0 border-white/10 md:border-r md:last:border-r-0 md:pl-6 md:first:pl-0">
                <p className="font-mono text-3xl md:text-4xl font-bold tracking-tight">{s.value}</p>
                <p className="text-[12px] text-white/40 mt-1 tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SELECTED WORK ═══ */}
      <section id="work" className="relative max-w-apple-wide mx-auto px-6 py-24 md:py-32">
        <Reveal>
          <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-4">
            <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.8rem,4vw,3.2rem)' }}>
              Selected Work
            </h2>
            <span className="font-mono text-[11px] tracking-[0.25em] text-white/35 uppercase">
              {String(FEATURED.length).padStart(2, '0')} Projects
            </span>
          </div>
        </Reveal>

        <div className="divide-y divide-white/[0.07]">
          {FEATURED.map((p, i) => (
            <Reveal key={p.to} delay={Math.min(i * 60, 180)}>
              <Link
                to={p.to}
                className="group grid md:grid-cols-[80px_1fr_auto] gap-4 md:gap-8 items-start py-10 md:py-12 transition-colors duration-300 hover:bg-white/[0.025] md:px-4 md:-mx-4 rounded-2xl"
              >
                <span className="font-mono text-[13px] text-white/30 pt-1.5 group-hover:text-white/60 transition-colors">
                  {p.no}
                </span>
                <div>
                  <p className="font-mono text-[10.5px] tracking-[0.25em] uppercase mb-3" style={{ color: p.accent }}>
                    {p.category}
                  </p>
                  <h3 className="text-2xl md:text-[2rem] font-bold tracking-tight mb-3 group-hover:translate-x-1 transition-transform duration-300">
                    {p.title}
                  </h3>
                  <p className="text-[14.5px] text-white/50 leading-relaxed max-w-2xl mb-4">{p.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.stack.map((t) => (
                      <span key={t} className="font-mono text-[10.5px] px-2.5 py-1 rounded-full border border-white/[0.12] text-white/45">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="hidden md:flex w-11 h-11 rounded-full border border-white/15 items-center justify-center mt-2 group-hover:bg-white group-hover:text-black group-hover:border-white transition-all duration-300">
                  <svg className="w-4 h-4 -rotate-45 group-hover:rotate-0 transition-transform duration-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12l-7.5 7.5M21 12H3" /></svg>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ ARCHIVE ═══ */}
      <section className="max-w-apple-wide mx-auto px-6 pb-24 md:pb-32">
        <Reveal>
          <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-2">
            <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.5rem,3vw,2.4rem)' }}>
              전체 프로젝트
            </h2>
            <span className="font-mono text-[11px] tracking-[0.25em] text-white/35 uppercase">Archive</span>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 md:gap-x-12">
          {ARCHIVE.map((a, i) => (
            <Link
              key={a.to}
              to={a.to}
              className="group flex items-center justify-between gap-4 py-4 border-b border-white/[0.07] hover:border-white/25 transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="font-mono text-[11px] text-white/25 w-6 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-[15px] font-medium text-white/75 group-hover:text-white truncate transition-colors">
                  {a.title}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-[10px] tracking-wider uppercase text-white/30 hidden sm:block">{a.cat}</span>
                <svg className="w-3.5 h-3.5 text-white/25 group-hover:text-white group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ═══ STACK ═══ */}
      <section className="border-t border-white/[0.07] bg-white/[0.015]">
        <div className="max-w-apple-wide mx-auto px-6 py-20 md:py-24">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/40 mb-8">Tech Stack</p>
            <div className="flex flex-wrap gap-2.5">
              {STACK.map((t) => (
                <span key={t} className="text-[13px] px-4 py-2 rounded-full border border-white/[0.1] text-white/60 hover:border-white/30 hover:text-white transition-colors cursor-default">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative overflow-hidden border-t border-white/[0.07]">
        <div className="absolute inset-0 opacity-[0.12]" style={{ background: 'radial-gradient(ellipse at 50% 120%,#6366f1,transparent 60%)' }} />
        <div className="relative max-w-apple-wide mx-auto px-6 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-bold tracking-tight mb-5" style={{ fontSize: 'clamp(1.9rem,4.5vw,3.4rem)' }}>
              직접 만져보는 게 가장 빠릅니다.
            </h2>
            <p className="text-white/50 max-w-md mx-auto mb-10 text-[15px] leading-relaxed">
              모든 프로젝트가 라이브로 돌아갑니다. AI 토론 한 판이면 이 플랫폼이 어떻게 움직이는지 알 수 있어요.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/battles" className="px-7 py-3.5 bg-white text-black text-[14px] font-semibold rounded-full hover:bg-white/85 transition-colors">
                AI 토론 시작하기
              </Link>
              {isAuthenticated ? (
                <Link to="/mypage" className="px-7 py-3.5 border border-white/20 hover:border-white/50 text-[14px] font-medium rounded-full transition-colors">
                  마이페이지
                </Link>
              ) : (
                <Link to="/register" className="px-7 py-3.5 border border-white/20 hover:border-white/50 text-[14px] font-medium rounded-full transition-colors">
                  무료 회원가입
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
